import React, { useState, useEffect } from "react";
import {
  Lock,
  Unlock,
  Pencil,
  Trash2,
  MoreVertical,
  Mail,
  Phone,
  Shield,
  KeyRound,
  Search,
} from "lucide-react";
import { activateUserApi, deleteUserApi, getInActiveUsers } from "../../../api/user";
import { apiBodyMessage } from "../../../utils/apiErrorMessage";
import Modal from "../../../components/modal";
import UnblockModal from "../../../components/unblockModal";
import PackageTabs from "../../../components/packageTabs";
import CreateUserBeartrack from "../../../components/createUserBeartrack/createUserBeartrack";
import { toast } from "../../../utils/toast";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Card } from "../../../components/ui/Card";
import { Button } from "../../../components/ui/Button";
import { Avatar } from "../../../components/ui/Avatar";
import { Skeleton } from "../../../components/ui/Skeleton";
import { EmptyState } from "../../../components/ui/EmptyState";
import { cn } from "../../../utils/cn";

function iniciales(name = "") {
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

export default function BeartrackBloquedUsers() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState();
  const [unblockModal, setUnBlockModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  // Input local: la búsqueda es server-side y se dispara al presionar Enter
  // (evita refetch por cada tecla), commiteando el valor a `searchTerm`.
  const [searchInput, setSearchInput] = useState("");
  // modales
  const [editMode, setEditMode] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [openModalUnblock, setOpenModalUnblock] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(1);

  const [users, setUsers] = useState([]);

  // Menú de acciones por card (token-based, reemplaza UncontrolledDropdown)
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    if (loading || typeof searchTerm === "string") {
      fetchUsers(currentPage).then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, currentPage, searchTerm]);

  // Cierra el menú de acciones al hacer click afuera
  useEffect(() => {
    const onClick = (e) => {
      if (openMenu && !e.target.closest(".lu-rowmenu")) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [openMenu]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setLoading(true);
  };

  const fetchUsers = async (page) => {
    try {
      const { data } = await getInActiveUsers(page, 10, searchTerm);
      // Defensivo: si el endpoint falla o cambia de shape, `data?.users` puede ser
      // undefined → `users.length`/`users.map` crasheaban. Caemos a [] (estado vacío).
      setUsers(Array.isArray(data?.users) ? data.users : []);
      setTotalPages(data?.totalPages || 1);
      setTotal(data?.total || 0);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching active users", error);
      setLoading(false);
      toast.error(t("Error al cargar usuarios"));
    }
  };

  const handleUnlock = async () => {
    if (!selectedUser?._id) return;
    setLoading(true);
    try {
      const res = await activateUserApi({
        userId: selectedUser._id,
        status: true,
      });
      setLoading(false);
      // activateUserApi never rejects: a failed PATCH arrives as { error }.
      if (res?.error) {
        toast.error(
          apiBodyMessage(res.error) || t("Error al desbloquear el usuario")
        );
        return; // modal stays open, no success toast
      }
      setOpenModalUnblock(false);
      toast.success(t("Usuario desbloqueado exitosamente"));
      fetchUsers(currentPage); // refresh list with the real state
    } catch {
      // Defensive: never surface axios transport text ("Network Error").
      setLoading(false);
      toast.error(t("Error al desbloquear el usuario"));
    }
  };

  const handleDelete = async () => {
    if (!selectedUser?._id) return;
    const response = await deleteUserApi({ userId: selectedUser._id });
    if (response?.error) {
      toast.error(t("Error al eliminar el usuario"));
      return;
    }
    setOpenModalDelete(false);
    toast.success(t("Usuario eliminado exitosamente"));
    setLoading(true);
  };

  const tabContent = [
    <CreateUserBeartrack
      closeModal={setOpenModalEdit}
      fetch={fetch}
      user={selectedUser}
      edit={editMode}
    />,
  ];

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setOpenModalEdit(true);
    setEditMode(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setOpenModalDelete(true);
  };

  const handleUnblockUser = (user) => {
    setSelectedUser(user);
    setOpenModalUnblock(true);
  };

  const menuOptions = [
    {
      title: t("Editar usuario"),
      subtitle: t("Modifica algunos datos como el correo, rol o contraseña."),
      icon: Pencil,
      onClick: handleEditUser,
    },
    {
      title: t("Desbloquear usuario"),
      subtitle: t("Permite al usuario volver a acceder a la plataforma."),
      icon: Unlock,
      onClick: handleUnblockUser,
    },
    {
      title: t("Eliminar usuario"),
      subtitle: t("Eliminar permanentemente este usuario. Esta acción le quita el acceso a la plataforma."),
      icon: Trash2,
      danger: true,
      onClick: handleDeleteUser,
    },
  ];

  const handleCloseModalEdit = () => {
    setOpenModalEdit(false);
    setEditMode(false);
  };

  return (
    <div className="w-full min-w-0 p-4 sm:p-6 space-y-5">
      {/* Modal eliminar */}
      <Modal
        size={"md"}
        open={openModalDelete}
        setOpen={() => setOpenModalDelete()}
        header={
          <div>
            <h1 className="text-xl font-semibold text-fg">{t("Eliminar usuario")}</h1>
          </div>
        }
        body={
          <p className="text-sm text-fg-muted">
            {t("Esta acción no se puede deshacer. Esto eliminará permanentemente la cuenta de usuario. Todos los datos serán eliminados y no tendrá acceso a la plataforma.")}
          </p>
        }
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setOpenModalDelete(false)}>
              {t("No, mantener")}
            </Button>
            <Button variant="danger" onClick={handleDelete}>{t("Sí, eliminar")}</Button>
          </div>
        }
      ></Modal>

      {/* Modal editar */}
      <Modal
        size={"md"}
        open={openModalEdit}
        setOpen={handleCloseModalEdit}
        header={
          <div>
            <h1 className="text-xl font-semibold text-fg">{t("Editar usuario")}</h1>
          </div>
        }
        body={<PackageTabs tabContent={tabContent} />}
      ></Modal>

      {/* Modal desbloquear */}
      <Modal
        size={"md"}
        open={openModalUnblock}
        setOpen={() => setOpenModalUnblock()}
        header={
          <div>
            <h1 className="text-xl font-semibold text-fg">{t("Desbloquear usuario")}</h1>
          </div>
        }
        body={
          <p className="text-sm text-fg-muted">
            {t("Esta acción desbloqueará al usuario seleccionado. Esta acción permitira al usuario acceder nuevamente a la plataforma.")}
          </p>
        }
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setOpenModalUnblock(false)}>
              {t("No, mantener")}
            </Button>
            <Button onClick={handleUnlock}>{t("Sí, desbloquear")}</Button>
          </div>
        }
      ></Modal>

      <Modal
        open={unblockModal}
        setOpen={() => setUnBlockModal(false)}
        body={
          <UnblockModal
            closeModal={setUnBlockModal}
            userId={selectedUser?._id}
            update={setLoading}
          />
        }
      />

      {/* Título y descripción */}
      <PageHeader
        icon={<Lock className="w-6 h-6 text-fg-muted" />}
        title={t("Usuarios inactivos")}
        subtitle={t("Gestiona y edita los usuarios inactivos/bloqueados.")}
      />

      <Card padding="md" className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-fg-muted">
            {total} {t("usuarios inactivos")}
          </span>
        </div>

        {/* Barra de búsqueda */}
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setSearchTerm(searchInput);
            }}
            placeholder={t("Buscar por nombre, correo o teléfono")}
            className="h-10 w-full rounded-lg border border-border bg-bg pl-9 pr-3 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        {/* Grid de usuarios — responsive (1 col móvil, 2 sm, 3 xl) */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={`sk-${i}`} padding="md" className="space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-2/3" />
              </Card>
            ))}
          </div>
        ) : users.length === 0 ? (
          <EmptyState
            icon={<Lock />}
            title={t("Sin usuarios inactivos")}
            description={t("No hay usuarios bloqueados o inactivos para mostrar.")}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {users.map((user) => (
              <Card key={user._id || user.id} padding="md" className="flex flex-col gap-3 transition-shadow hover:shadow-elevated">
                {/* Cabecera de la card */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Avatar initials={iniciales(user.name)} size="md" tone="neutral" />
                    <span className="text-sm font-semibold text-fg truncate">{user.name}</span>
                  </div>
                  <div className="lu-rowmenu relative shrink-0">
                    <button
                      type="button"
                      aria-label={t("Opciones")}
                      onClick={() => setOpenMenu(openMenu === (user._id || user.id) ? null : (user._id || user.id))}
                      className="p-1.5 rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {openMenu === (user._id || user.id) && (
                      <div className="absolute right-0 top-9 z-20 w-72 max-w-[calc(100vw-1.5rem)] rounded-xl border border-border bg-bg shadow-lg p-1.5">
                        {menuOptions.map((item, idx) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => { setOpenMenu(null); item.onClick(user); }}
                              className={cn(
                                "w-full flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors",
                                item.danger
                                  ? "text-status-error hover:bg-status-error-soft"
                                  : "text-fg hover:bg-bg-muted",
                              )}
                            >
                              <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                              <span className="min-w-0">
                                <span className="block text-sm font-medium">{item.title}</span>
                                <span className={cn("block text-xs", item.danger ? "text-status-error/80" : "text-fg-muted")}>
                                  {item.subtitle}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Datos */}
                <dl className="divide-y divide-border text-sm">
                  <div className="flex items-center justify-between gap-2 py-2">
                    <dt className="inline-flex items-center gap-1.5 text-fg-muted">
                      <Shield className="w-3.5 h-3.5" /> {t("Rol")}
                    </dt>
                    <dd className="text-right text-fg truncate">{user.role || "—"}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-2 py-2">
                    <dt className="inline-flex items-center gap-1.5 text-fg-muted">
                      <Phone className="w-3.5 h-3.5" /> {t("Teléfono")}
                    </dt>
                    <dd className="text-right text-fg truncate">{user.phone || "—"}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-2 py-2">
                    <dt className="inline-flex items-center gap-1.5 text-fg-muted">
                      <Mail className="w-3.5 h-3.5" /> {t("Correo")}
                    </dt>
                    <dd className="text-right text-fg truncate">{user.email || "—"}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-2 py-2">
                    <dt className="inline-flex items-center gap-1.5 text-fg-muted">
                      <KeyRound className="w-3.5 h-3.5" /> {t("Contraseña")}
                    </dt>
                    <dd className="text-right text-fg-subtle tracking-widest">•••••••••</dd>
                  </div>
                </dl>
              </Card>
            ))}
          </div>
        )}

        {/* Controles de paginación */}
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {t("Anterior")}
          </Button>
          <span className="text-sm text-fg-muted">
            {t("Página")} {currentPage} {t("de")} {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {t("Siguiente")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
